import {Component, computed, inject, Input, signal} from '@angular/core';
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {IActiveGameInstance} from "../../interfaces/IActiveGameInstance";
import {PipesModule} from "../../pipes/pipes.module";
import {GameStateEnum} from "../../enum/GameStateEnum";
import {HasPermissionDirective} from "../../directives/permissions/has-permission";
import {MatButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {PermissionTypes} from "../../enum/PermissionTypes";
import {DatePipe, NgTemplateOutlet} from "@angular/common";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {GameService} from "../../services/game.service";
import {GameInstanceService} from "../../services/game-instance.service";
import {SnackbarService} from "../../services/snackbar.service";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {ActiveUserService} from "../../services/active-user.service";
import {IForceDrawWinner} from "../../interfaces/IForceDrawWinner";
import {IApiErrorResponse} from "../../interfaces/error/IApiErrorResponse";
import {GameTypeEnum} from "../../enum/GameTypeEnum";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {concatMap, of} from 'rxjs';
import {PauseGameDialogComponent} from '../dialogs/pause-game-dialog/pause-game-dialog.component';
import {StopGameDialogComponent} from '../dialogs/stop-game-dialog/stop-game-dialog.component';
import {EndGameDialogComponent} from '../dialogs/end-game-dialog/end-game-dialog.component';
import {
  ChangeUnpauseDateDialogComponent
} from '../dialogs/change-unpause-date-dialog/change-unpause-date-dialog.component';
import {UnpauseGameDialogComponent} from '../dialogs/unpause-game-dialog/unpause-game-dialog.component';
import {ComponentType} from "@angular/cdk/overlay";

@Component({
  selector: 'app-game-state-settings',
  standalone: true,
  imports: [
    MatCardHeader,
    MatCard,
    MatCardTitle,
    MatCardContent,
    PipesModule,
    HasPermissionDirective,
    MatButton,
    MatIcon,
    MatTooltip,
    DatePipe,
    NgTemplateOutlet,
    MatProgressSpinner,
    MatSlideToggle
  ],
  templateUrl: './game-state-settings.component.html',
  styleUrl: './game-state-settings.component.css'
})
export class GameStateSettingsComponent {
  @Input() set activeGame(value: IGameQueryResult | null) {
    this._activeGame.set(value);
    this.loadActiveDraw(value);
  }

  get activeGame(): IGameQueryResult | null {
    return this._activeGame();
  }

  private readonly _activeGame = signal<IGameQueryResult | null>(null);
  private readonly _activeDraw = signal<IActiveGameInstance | null>(null);
  protected readonly activeDraw = this._activeDraw.asReadonly();

  // Separates "no draw is running" from "still fetching" - without it the card claims the former for the
  // length of the round trip.
  private readonly _activeDrawLoaded = signal(false);
  protected readonly activeDrawLoaded = this._activeDrawLoaded.asReadonly();

  private gameService: GameService = inject(GameService);
  private gameInstanceService: GameInstanceService = inject(GameInstanceService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private matDialog: MatDialog = inject(MatDialog);

  public uiState = UIStateEnum.ShowData;
  public uiStateEnum = UIStateEnum;
  protected readonly gameStateEnum = GameStateEnum;
  protected readonly gameTypeEnum = GameTypeEnum;
  protected readonly permissionTypes = PermissionTypes;

  public activeUser = this.activeUserService.activeUser();

  protected readonly isGamePausedOrPendingPause = computed(() => {
    const game = this._activeGame();
    if (!game) return false;

    const pauseStates = [
      GameStateEnum.Paused,
      GameStateEnum.NoLicense,
      GameStateEnum.PauseAfterNextDraw,
      GameStateEnum.PauseAfterNextWinner,
      GameStateEnum.PauseAfterNextDrawCancelAutoplays,
      GameStateEnum.PauseAfterNextWinnerCancelAutoplays
    ];

    return pauseStates.includes(game.State);
  });

  openPauseGame() {
    this.openDialogAndRefreshGame(PauseGameDialogComponent, {
      game: this.activeGame,
      draw: this._activeDraw()
    });
  }

  openStopGame() {
    this.openDialogAndRefreshGame(StopGameDialogComponent, this.activeGame);
  }

  openEndGame() {
    this.openDialogAndRefreshGame(EndGameDialogComponent, {
      game: this.activeGame,
      draw: this._activeDraw()
    });
  }

  openChangeUnpauseDate() {
    this.openDialogAndRefreshGame(ChangeUnpauseDateDialogComponent, {
      game: this.activeGame
    });
  }

  openUnpauseGame() {
    this.openDialogAndRefreshGame(UnpauseGameDialogComponent, {
      game: this.activeGame
    });
  }

  updateGameShouldForceWinnerStatus() {
    const game = this._activeGame();

    if (!game || !this._activeDraw()) {
      this.snackbarService.openErrorSnackBar("There is no draw currently running for this game, so a winner cannot be forced.");
      return;
    }

    this.uiState = UIStateEnum.ShowLoading;

    const command: IForceDrawWinner = {
      AdminId: this.activeUser.Id,
      GameId: game.Id
    };

    this.gameService.updateGameForceWinner(command)
      .pipe(concatMap(() => this.gameInstanceService.activeGameInstanceByGame(game.Id, this.activeUser.Id)))
      .subscribe({
        next: (updatedDrawP: IActiveGameInstance) => {
          // Read the flag back off the server instead of flipping a local copy: the command toggles
          // whatever the row currently holds, so the re-fetched instance is the only reliable truth.
          this._activeDraw.set(updatedDrawP);
          this.uiState = UIStateEnum.ShowData;
          this.snackbarService.openSuccessfulSnackBar(updatedDrawP.ForceWinner
            ? `Update successful. The current draw for ${game.Name} will force a winner.`
            : `Update successful. The current draw for ${game.Name} will no longer force a winner.`);
        },
        error: () => {
          this.uiState = UIStateEnum.ShowData;
          this.snackbarService.openErrorSnackBar("Failed to update the force winner status.");
        }
      });
  }

  private loadActiveDraw(gameP: IGameQueryResult | null) {
    this._activeDraw.set(null);
    this._activeDrawLoaded.set(false);

    if (!gameP) {
      this._activeDrawLoaded.set(true);
      return;
    }

    this.gameInstanceService.activeGameInstanceByGame(gameP.Id, this.activeUser.Id).subscribe({
      next: (activeDrawP: IActiveGameInstance) => {
        this._activeDraw.set(activeDrawP);
        this._activeDrawLoaded.set(true);
      },
      error: (errorP: IApiErrorResponse) => {
        // Settled either way: a 404 is the real "no draw" answer, and other errors must not leave it looking.
        this._activeDrawLoaded.set(true);

        // A paused, stopped or ended game legitimately has no active instance. That is a state this
        // card is meant to render, not an error worth interrupting the admin over.
        if (errorP?.Status === 404) {
          return;
        }

        this.snackbarService.openErrorSnackBar('Could not load the current draw for this game. Some game state actions will be unavailable.');
      }
    });
  }

  private openDialogAndRefreshGame<T>(component: ComponentType<T>, data: unknown) {
    const config: MatDialogConfig = {
      data,
      width: '650px',
    };

    this.matDialog.open(component, config)
      .afterClosed()
      .pipe(concatMap((res) => {
        if (res && this.activeGame) {
          return this.gameService.getGameByGameId(this.activeGame.Id);
        }
        return of(null);
      }))
      .subscribe({
        next: (res: IGameQueryResult | null) => {
          if (res) {
            this._activeGame.set(res);
            // Pausing, stopping or ending the game changes the instance too, so the draw this card
            // is holding is stale the moment the refreshed game comes back.
            this.loadActiveDraw(res);
          }
        },
        error: () => {
          this.snackbarService.openErrorSnackBar('Looks like there was an error retrieving your game info. Please try again or contact a system admin.');
        }
      });
  }
}
